/**
 * @name useLogo
 * @description Hook para carregar logos dinamicamente da configuração ou usar padrão
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import useUserContext from '@/contexts/UserContext/useUserContext';
import { configuracoesService } from '@/services/supabase/entities/ConfiguracoesService';
import useThemeContext from '@/themes/hooks/useThemeContext';
import { useEffect, useState } from 'react';

// URLs das logos locais como fallback
import logoDark from '@/assets/png/logo-dark.png';
import logoLight from '@/assets/png/logo-light.png';
import logoDefault from '@/assets/png/logo.png';

interface UseLogoReturn {
     logoUrl: string;
     logoLightUrl: string;
     logoDarkUrl: string;
     currentLogo: string;
     isLoading: boolean;
     refreshLogos: () => Promise<void>;
}

export const useLogo = (): UseLogoReturn => {
     const { userData } = useUserContext();
     const { theme } = useThemeContext();
     const [logoUrls, setLogoUrls] = useState({
          logo_url: logoDefault,
          logo_light_url: logoLight,
          logo_dark_url: logoDark,
     });
     const [isLoading, setIsLoading] = useState(false);

     const loadLogos = async () => {
          if (!userData?.fk_id_organizacao) return;

          setIsLoading(true);
          try {
               const configLogos = await configuracoesService.getLogoUrls(userData.fk_id_organizacao);
               
               setLogoUrls({
                    logo_url: configLogos.logo_url || logoDefault,
                    logo_light_url: configLogos.logo_light_url || logoLight,
                    logo_dark_url: configLogos.logo_dark_url || logoDark,
               });
          } catch (error) {
               console.error('Erro ao carregar logos da configuração:', error);
               // Manter logos padrão em caso de erro
          } finally {
               setIsLoading(false);
          }
     };

     useEffect(() => {
          loadLogos();
     }, [userData?.fk_id_organizacao]);

     const currentLogo = theme.palette.mode === 'dark' 
          ? logoUrls.logo_dark_url 
          : logoUrls.logo_light_url;

     return {
          logoUrl: logoUrls.logo_url,
          logoLightUrl: logoUrls.logo_light_url,
          logoDarkUrl: logoUrls.logo_dark_url,
          currentLogo,
          isLoading,
          refreshLogos: loadLogos,
     };
};
